import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../Firebase/firebaseconfig";
import "./ListaPropiedades.css";
import { FaPlus, FaSearch, FaEdit } from "react-icons/fa";
import NuevoFraccionamientoModal from "../Fraccionamientos/NuevoFraccionamientoModal";
import { searchPropiedadesFlexible } from "../../helpers/searchPropiedades";

const PROPS_POR_PAGINA = 20;

const ListaPropiedades = () => {
  const [propiedades, setPropiedades] = useState([]);
  const [allPropiedades, setAllPropiedades] = useState([]);
  const [fraccionamientos, setFraccionamientos] = useState([]);
  const [fraccFiltro, setFraccFiltro] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("todas");
  const [openFraccModal, setOpenFraccModal] = useState(false);
  const [fraccEditando, setFraccEditando] = useState(null);
  const [loading, setLoading] = useState(false);
  const [intent, setIntent] = useState("");
  const [paginaActual, setPaginaActual] = useState(1);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const cargarPropiedades = useCallback(async () => {
    const snap = await getDocs(collection(db, "propiedades"));

    const items = snap.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    setPropiedades(items);
    setAllPropiedades(items);
  }, []);

  const cargarFraccionamientos = useCallback(async () => {
    const snap = await getDocs(collection(db, "fraccionamientos"));

    const items = snap.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    setFraccionamientos(items);
  }, []);

  useEffect(() => {
    cargarPropiedades();
    cargarFraccionamientos();
  }, [cargarPropiedades, cargarFraccionamientos]);

  const fraccMap = useMemo(() => {
    const map = {};
    fraccionamientos.forEach((f) => {
      map[f.id] = f.nombre;
    });
    return map;
  }, [fraccionamientos]);

  const fraccionamientoSeleccionado = useMemo(
    () => fraccionamientos.find((f) => f.id === fraccFiltro) || null,
    [fraccionamientos, fraccFiltro]
  );

  useEffect(() => {
    const fraccFromUrl = searchParams.get("fracc") || "";
    const shouldEdit = searchParams.get("editarFracc") === "1";

    if (!fraccFromUrl || fraccionamientos.length === 0) return;

    const selected = fraccionamientos.find((f) => f.id === fraccFromUrl);
    if (!selected) return;

    setFraccFiltro(fraccFromUrl);

    if (shouldEdit) {
      setFraccEditando(selected);
      setOpenFraccModal(true);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("editarFracc");
          return next;
        },
        { replace: true }
      );
    }
  }, [fraccionamientos, searchParams, setSearchParams]);

  const debouncedTerm = useDebounce(busqueda, 350);

  useEffect(() => {
    const run = async () => {
      if (!debouncedTerm.trim()) {
        setIntent("");
        setPropiedades(allPropiedades);
        return;
      }

      setLoading(true);

      try {
        const { items, intent } = await searchPropiedadesFlexible(
          db,
          debouncedTerm,
          fraccFiltro || ""
        );

        setPropiedades(items);
        setIntent(intent);
      } catch (e) {
        console.error("search error:", e);
        setIntent("ERROR");
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [debouncedTerm, fraccFiltro, allPropiedades]);

  const listaFinal = useMemo(() => {
    let list = propiedades;

    list = list.filter((p) => p.activo !== false);

    if (filtroEstado !== "todas") {
      list = list.filter((p) => p.estado === filtroEstado);
    }

    if (!debouncedTerm.trim() && fraccFiltro) {
      list = list.filter((p) => p.fraccionamientoId === fraccFiltro);
    }

    return list;
  }, [propiedades, filtroEstado, fraccFiltro, debouncedTerm]);

  const totalPaginas = useMemo(() => {
    return Math.max(1, Math.ceil(listaFinal.length / PROPS_POR_PAGINA));
  }, [listaFinal.length]);

  const propiedadesPagina = useMemo(() => {
    const inicio = (paginaActual - 1) * PROPS_POR_PAGINA;
    return listaFinal.slice(inicio, inicio + PROPS_POR_PAGINA);
  }, [listaFinal, paginaActual]);

  useEffect(() => {
    setPaginaActual(1);
  }, [busqueda, debouncedTerm, fraccFiltro, filtroEstado]);

  useEffect(() => {
    if (paginaActual > totalPaginas) {
      setPaginaActual(totalPaginas);
    }
  }, [paginaActual, totalPaginas]);

  const precioFmt = useCallback((p) => {
    const val = p.precioContado ?? p.precio ?? 0;
    return Number(val).toLocaleString();
  }, []);

  const fraccNombreDe = useCallback(
    (prop) => {
      return prop.fraccNombre || fraccMap[prop.fraccionamientoId] || "-";
    },
    [fraccMap]
  );

  return (
    <div className="clientes-container">
      <h2 className="titulo-centro">Gestión de Propiedades</h2>

      <div className="clientes-header">
        <div
          className="filtros-superiores"
          style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}
        >
          <input
            type="text"
            placeholder={`Buscar${fraccFiltro ? " en fraccionamiento" : ""}: PR-00012, MZ7-LT3, 7-3, 'Peral', 'Terreno'...`}
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="busqueda-input"
            style={{ height: 40, minWidth: 280 }}
          />

          <select
            value={fraccFiltro}
            onChange={(e) => setFraccFiltro(e.target.value)}
            className="orden-select"
            style={{ height: 40, minWidth: 220 }}
          >
            <option value="">Todos los fraccionamientos</option>

            {fraccionamientos.map((f) => (
              <option key={f.id} value={f.id}>
                {f.nombre} — {f.ubicacion}
              </option>
            ))}
          </select>

          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="orden-select"
            style={{ height: 40 }}
          >
            <option value="todas">Todas</option>
            <option value="disponible">Disponibles</option>
            <option value="reservada">Reservadas</option>
            <option value="vendida">Vendidas</option>
          </select>

          <button
            className="nuevo-cliente-button"
            style={{ height: 40 }}
            onClick={() => {
              setFraccEditando(null);
              setOpenFraccModal(true);
            }}
          >
            <FaPlus style={{ marginRight: 6 }} /> Nuevo fraccionamiento
          </button>

          <button
            className="nuevo-cliente-button"
            style={{ height: 40, background: fraccionamientoSeleccionado ? "#334155" : "#94a3b8" }}
            disabled={!fraccionamientoSeleccionado}
            onClick={() => {
              if (!fraccionamientoSeleccionado) return;
              setFraccEditando(fraccionamientoSeleccionado);
              setOpenFraccModal(true);
            }}
            title={
              fraccionamientoSeleccionado
                ? "Editar el fraccionamiento seleccionado"
                : "Selecciona un fraccionamiento para editarlo"
            }
          >
            <FaEdit style={{ marginRight: 6 }} /> Editar fraccionamiento
          </button>

          <button
            className="nuevo-cliente-button"
            style={{ height: 40 }}
            onClick={() => navigate("/app/propiedades/nueva")}
          >
            <FaPlus style={{ marginRight: 6 }} /> Nueva Propiedad
          </button>

          <button
            className="nuevo-cliente-button"
            style={{ height: 40 }}
            onClick={() => navigate("/app/propiedades/fraccionamiento/nuevo")}
          >
            <FaPlus style={{ marginRight: 6 }} /> Nueva Manzana o Grupo
          </button>
        </div>

        {intent ? (
          <div style={{ marginTop: -6, marginBottom: 6, opacity: 0.8, fontSize: 12 }}>
            Modo de búsqueda:{" "}
            <span style={{ padding: "2px 8px", borderRadius: 12, background: "#eef" }}>
              {intent}
            </span>

            {intent === "MZ/LT" && !fraccFiltro
              ? " · Sugerencia: selecciona un fraccionamiento para acotar"
              : null}
          </div>
        ) : (
          <small className="subetiqueta-campo">
            Escribe: <em>PR-00012</em>, <em>MZ7-LT3</em>, <em>7-3</em> o texto como{" "}
            <em>Peral</em>, <em>Terreno</em>, <em>Centro</em>.
          </small>
        )}
      </div>

      {loading && (
        <div className="spinner" style={{ marginTop: 16 }}>
          Buscando…
        </div>
      )}

      <div className="table-wrapper">
        <table className="clientes-table">
          <thead>
            <tr>
              <th>Folio</th>
              <th>Nombre</th>
              <th>Fraccionamiento</th>
              <th>Estado</th>
              <th>Precio</th>
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {propiedadesPagina.map((p) => (
              <tr key={p.id}>
                <td data-label="Folio">{p.folio}</td>
                <td data-label="Nombre">{p.nombre || "-"}</td>
                <td data-label="Fraccionamiento">{fraccNombreDe(p)}</td>
                <td data-label="Estado">{p.estado}</td>
                <td data-label="Precio">${precioFmt(p)}</td>
                <td data-label="Acciones">
                  <button
                    className="detalle-btn"
                    onClick={() => navigate(`/app/propiedades/${p.id}`)}
                  >
                    <FaSearch />
                  </button>

                  <button
                    className="detalle-btn"
                    onClick={() => navigate(`/app/propiedades/${p.id}/editar`)}
                  >
                    <FaEdit />
                  </button>
                </td>
              </tr>
            ))}

            {!loading && propiedadesPagina.length === 0 && (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", opacity: 0.7 }}>
                  Sin resultados
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!loading && listaFinal.length > 0 && (
        <div
          className="paginador"
          style={{
            display: "flex",
            gap: 12,
            justifyContent: "center",
            alignItems: "center",
            marginTop: 16,
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={() => setPaginaActual((p) => Math.max(1, p - 1))}
            disabled={paginaActual === 1}
          >
            Anterior
          </button>

          <span>
            Página {paginaActual} de {totalPaginas}
          </span>

          <button
            onClick={() => setPaginaActual((p) => Math.min(totalPaginas, p + 1))}
            disabled={paginaActual === totalPaginas}
          >
            Siguiente
          </button>
        </div>
      )}

      <NuevoFraccionamientoModal
        open={openFraccModal}
        fraccionamiento={fraccEditando}
        onClose={() => {
          setOpenFraccModal(false);
          setFraccEditando(null);
        }}
        onCreated={() => {
          setOpenFraccModal(false);
          setFraccEditando(null);
          cargarFraccionamientos();
        }}
        onUpdated={() => {
          setOpenFraccModal(false);
          setFraccEditando(null);
          cargarFraccionamientos();
        }}
      />
    </div>
  );
};

function useDebounce(value, delay = 350) {
  const [v, setV] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);

  return v;
}

export default ListaPropiedades;
